<template>
  <div
    class="quantity-selector"
    :class="{
      'quantity-selector--large': large,
      [$class('error')]: state === 'error',
    }"
  >
    <div class="quantity-selector__control quantity-selector__control--button">
      <button
        class="quantity-selector__button"
        :class="{ [$class('disabled')]: disableDecrease }"
        :tabindex="disableDecrease && -1"
        type="button"
        @click="handleQuantityUpdate('decrease')"
      >
        <span
          class="visually-hidden"
          v-text="$string('cart.line_item.decrease_quantity')"
        />

        <icon-minus />
      </button>
    </div>

    <div class="quantity-selector__control quantity-selector__control--input">
      <label
        class="visually-hidden"
        :for="`quantity-selector-input-${lineItem ? lineItem.key : 'product'}`"
        v-text="$string('cart.line_item.quantity')"
      />

      <input
        :id="`quantity-selector-input-${lineItem ? lineItem.key : 'product'}`"
        ref="quantityInput"
        class="quantity-selector__input"
        :name="name"
        pattern="[0-9]*"
        :readonly="readOnlyInput"
        :tabindex="readOnlyInput && -1"
        type="number"
        :value="quantity"
        @input="handleQuantityInput"
        @blur="handleQuantityValidation"
      >
    </div>

    <div class="quantity-selector__control quantity-selector__control--button">
      <button
        class="quantity-selector__button"
        :class="{ [$class('disabled')]: disableIncrease }"
        :tabindex="disableIncrease && -1"
        type="button"
        @click="handleQuantityUpdate('increase')"
      >
        <span
          class="visually-hidden"
          v-text="$string('cart.line_item.increase_quantity')"
        />

        <icon-plus />
      </button>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Quantity selector (quantity-selector)
 * -----------------------------------------------------------------------------
 * Base template and functionality for quantity selector.
 * - Used in product form and line item.
 * - Responsible for emitting events to update quantity, parent components must
 *   action the update.
 *
 * @param {Boolean} [large] - Large styles.
 * @param {Object} [lineItem] - Line item object.
 * @param {Number} [min] - The minimum value the selector can be.
 * @param {Number} [max] - The maximum value the selector can be.
 * @param {Number} quantity - Actual line item quantity selector value as
 * reflected in Shopify's data.
 * @param {String} state - Parent component state.
 * @param {Boolean} [readOnlyInput] - set quantity input to read only
 *
 * @emits updateQuantity - Emit new quantity value.
 *
 */
import IconMinus from '~icons/directional-navigation/minus.svg'
import IconPlus from '~icons/directional-navigation/plus.svg'

export default {
  name: 'QuantitySelector',

  components: {
    IconMinus,
    IconPlus,
  },

  props: {
    large: {
      type: Boolean,
      default: false,
    },
    lineItem: {
      type: [Boolean, Object],
      default: false,
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: [Boolean, Number],
      default: false,
    },
    quantity: {
      type: Number,
      default: 1,
      required: true,
    },
    readOnlyInput: {
      type: Boolean,
      default: true,
    },
    state: {
      type: String,
      default: 'loading',
      required: true,
    },
  },

  emits: {
    'update-quantity': (payload) => typeof payload === 'number',
  },

  data() {
    return {
      localQuantity: 1,
    }
  },

  computed: {

    /**
     * Compute if decrease button should be disabled.
     * @return {Boolean}
     */
    disableDecrease() {
      return (
        this.unavailable ||
        this.state === 'updating' ||
        this.state === 'loading' ||
        this.localQuantity === this.min
      )
    },

    /**
     * Compute if increase button should be disabled.
     * @return {Boolean}
     */
    disableIncrease() {
      const outOfInventory =
        (this.lineItem.inventory_management === 'shopify' &&
          this.lineItem.inventory_policy === 'deny' &&
          this.quantity >= this.inventory) ||
        (this.localQuantity >= this.max)

      return (
        this.unavailable ||
        this.state === 'updating' ||
        this.state === 'loading' ||
        outOfInventory
      )
    },

    /**
     * Compute inventory.
     * - Returns 0 if no limit.
     * @returns {Number}
     */
    inventory() {
      return this.lineItem?.inventory_quantity || 0
    },

    /**
     * Compute input name.
     * @return {String}
     */
    name() {
      if (this.lineItem?.key) {
        return 'updates[]'
      }

      return 'quantity'
    },

    /**
     * Compute unavailable.
     * - If object has available = false and no quantity, means it's unavailable
     *   on the product page.
     * @returns {Boolean}
     */
    unavailable() {
      if (!this.lineItem) {
        return false
      }

      return this.lineItem.available === false && !this.lineItem.quantity
    },
  },

  watch: {

    /**
     * Watch actual quantity to update local quantity.
     */
    quantity() {
      this.localQuantity = this.quantity
    },
  },

  mounted() {
    this.localQuantity = Math.max(this.quantity, this.min)
  },

  methods: {

    /**
     * Handle quantity selector update.
     * - Buttons only update local quantity which emits an event.
     * - If the event is successful then the quantity prop is updated and this
     *   is used for front-end display.
     * @param {String | Number} type - `decrease`, `increase` or quantity.
     */
    handleQuantityUpdate(type) {
      if (
        this.state === 'updating' ||
        (type === 'decrease' && this.disableDecrease) ||
        (type === 'increase' && this.disableIncrease)
      ) {
        return
      }

      if (type === 'decrease') {
        this.localQuantity -= this.min || 1
      } else if (type === 'increase') {
        this.localQuantity += this.min || 1
      } else {
        this.localQuantity = Number(type)
      }

      this.$emit('update-quantity', this.localQuantity)
    },

    /**
     * Handle quantity input update.
     */
    handleQuantityInput() {
      const quantityInput = this.$refs.quantityInput
      const inputQuantity = quantityInput.value
        ? Number(quantityInput.value)
        : this.quantity

      this.handleQuantityUpdate(inputQuantity)
    },

    /**
     * Handle quantity input validation on blur.
     */
    handleQuantityValidation() {
      const quantityInput = this.$refs.quantityInput
      const minIncrement = this.min || 1

      let inputQuantity = Number(quantityInput.value)

      if (!quantityInput.value.length || quantityInput.value < this.min) {
        inputQuantity = minIncrement
        quantityInput.value = minIncrement

      } else if (this.max && quantityInput.value > this.max) {
        inputQuantity = this.max
      }

      /* Match input quantity to minimum quantity increment e.g
       * if increment is 6, input should be rounded up to highest 6
       */
      if (inputQuantity % minIncrement !== 0) {
        const difference = Math.ceil(inputQuantity / minIncrement) * minIncrement

        inputQuantity = difference

        if (this.max && inputQuantity > this.max) {
          inputQuantity -= minIncrement
        }
      }

      this.handleQuantityUpdate(inputQuantity)
    },

  },
}
</script>

<style lang="scss">
@import './quantity-selector';
</style>
